import {announce} from '@github-ui/aria-live'
import {eventToHotkeyString} from '@github-ui/hotkey'
import {isNonCharacterKeyShortcut} from '@github-ui/hotkey/keyboard-shortcuts-helper'
import useColorModes from '@github-ui/react-core/use-color-modes'
// eslint-disable-next-line no-restricted-imports
import {ScreenSize} from '@github-ui/screen-size'
import {ScopedCommands} from '@github-ui/ui-commands'
import {useCodeViewOptions} from '@github-ui/use-code-view-options'
import {clsx} from 'clsx'
import {useState} from 'react'

import {useCurrentLineHeight} from '../../../../hooks/use-current-line-height'
import {minLeftOffsetBlob, scrollRightOffset, useIsCursorEnabled} from '../../../../hooks/use-cursor-navigation'
import {textAreaId} from '../../../../utilities/blob-element-ids'
import {calculateLineNumberFromOffset} from '../../../../utilities/lines'
import styles from './TextArea.module.css'
import {TextAreaHelpDialog} from './TextAreaHelpDialog'

//this is the height of the content that exists above where the code lines begin. This is also the
//approximation for users that do not have the staff bar enabled, which I think is the better choice to use
//it also highly likely will never be needed, but in the off chance something goes wrong this fallback is here
//to help approximate the correct offset
const approximateBlobYOffsetDesktop = 354
const approximateBlobYOffsetMobile = 423

const isTypingEvent = (e: React.KeyboardEvent) => !isNonCharacterKeyShortcut(eventToHotkeyString(e.nativeEvent))

export function TextArea({
  textAreaRef,
  setTextOverlayShouldBeVisible,
  setTextSelection,
  cursorClickStartRef,
  parentRef,
  tabSize,
  plainTextLinesAsString,
  numLines,
  setIsTextAreaFocused,
}: {
  textAreaRef: React.RefObject<HTMLTextAreaElement | null>
  setTextOverlayShouldBeVisible: (shouldRender: boolean) => void
  setTextSelection: ({
    start,
    end,
    keyboard,
  }: {
    start: number
    end: number
    keyboard: boolean
    displayStart: boolean
  }) => void
  cursorClickStartRef: React.RefObject<{startX: number; startY: number}> | undefined
  parentRef: React.RefObject<HTMLDivElement | null>
  tabSize: number
  plainTextLinesAsString: string
  numLines: number
  setIsTextAreaFocused: (value: React.SetStateAction<boolean>) => void
}) {
  const shouldUseCursor = useIsCursorEnabled()
  const wrapOption = useCodeViewOptions().codeWrappingOption
  const lineHeight = useCurrentLineHeight('react-line-numbers')

  const textAreaHeight = lineHeight * (numLines + 1)
  const [helpDialogOpen, setHelpDialogOpen] = useState(false)
  const {colorMode, dayScheme, nightScheme} = useColorModes()
  const activeScheme = colorMode === 'night' ? nightScheme : dayScheme
  const isHighContrast = activeScheme?.endsWith('high_contrast')

  function handleKeydownWithinTextArea(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    // setTimeout allows other handlers (UI commands) to try handling this first
    setTimeout(() => {
      // If user tries to type in the input (other than registered UI commands, which prevent default), show the hint
      if (!e.defaultPrevented && isTypingEvent(e)) {
        announce('Code view is read only.')
        setTextOverlayShouldBeVisible(true)
        return
      }

      // Otherwise hide hint and update selection state
      setTextOverlayShouldBeVisible(false)
      requestAnimationFrame(() => {
        if (textAreaRef.current) {
          setTextSelection({
            start: textAreaRef.current.selectionStart,
            end: textAreaRef.current.selectionEnd,
            keyboard: true,
            displayStart: false,
          })
        }
      })
    })
  }

  return (
    <ScopedCommands commands={{'code-view:open-cursor-nav-help-dialog': () => setHelpDialogOpen(true)}}>
      <textarea
        id={textAreaId}
        data-testid={textAreaId}
        ref={textAreaRef}
        onMouseUp={event =>
          mouseUpHandler(
            event,
            textAreaRef,
            setTextOverlayShouldBeVisible,
            setTextSelection,
            cursorClickStartRef,
            shouldUseCursor,
            parentRef,
            lineHeight,
          )
        }
        onMouseDown={event => mouseDownHandler(event, parentRef, shouldUseCursor, cursorClickStartRef, lineHeight)}
        aria-label={'file content'}
        aria-readonly
        //this prevents the virtual keyboard from being popped up when a user is on mobile
        inputMode={'none'}
        tabIndex={0}
        aria-multiline
        aria-haspopup={false}
        //needed to disable grammarly so that random code snippets don't get highlighted as grammar errors
        data-gramm="false"
        data-gramm_editor="false"
        data-enable-grammarly="false"
        style={{
          resize: 'none',
          marginTop: -2,
          paddingLeft: minLeftOffsetBlob,
          paddingRight: scrollRightOffset,
          display: 'hidden',
          width: '100%',
          backgroundColor: 'unset',
          boxSizing: 'border-box',
          color: 'transparent',
          position: 'absolute',
          border: 'none',
          tabSize,
          outline: 'none',
          overflowX: 'auto',
          height: textAreaHeight,
          fontSize: '12px',
          lineHeight: '20px',
          overflowY: 'hidden',
          overflowWrap: wrapOption.enabled ? 'anywhere' : 'normal',
          overscrollBehaviorX: 'none',
          whiteSpace: wrapOption.enabled ? 'pre-wrap' : 'pre',
          zIndex: 1,
        }}
        value={plainTextLinesAsString}
        onKeyDown={handleKeydownWithinTextArea}
        spellCheck={false}
        autoCorrect="off"
        autoCapitalize="off"
        autoComplete="off"
        data-ms-editor="false"
        onDrop={e => {
          //if a user dropped a URL on the text area, open it in a new tab like it normally would instead of eating it
          const text = e.dataTransfer.getData('Text')
          try {
            // eslint-disable-next-line no-restricted-syntax
            const url = new URL(text)
            window.open(url, '_blank')?.focus()
          } catch {
            //the thing dropped was not a URL, catch but don't do anything
          }
          return false
        }}
        onPaste={e => {
          e.preventDefault()
          return false
        }}
        onChange={() => {
          //need an empty on change handler to prevent react from complaining about a controlled component
          //we don't actually want to change the value on the textarea because it's readonly
          //but we can't put the readonly attribute on the textarea because then it won't be focusable
        }}
        className={clsx(
          styles.reactBlobTextarea,
          /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
          'react-blob-print-hide',
          isHighContrast && styles.selectContrast,
        )}
        onFocus={() => {
          setIsTextAreaFocused(true)
        }}
        onBlur={() => {
          setTextOverlayShouldBeVisible(false)
        }}
      />
      {helpDialogOpen && (
        <TextAreaHelpDialog
          onDismiss={() => {
            setHelpDialogOpen(false)
          }}
        />
      )}
    </ScopedCommands>
  )
}

//mouseUpHandler will always fire before the onClick handler, so we will reset the information in the click handler
//if the user did a double or triple click
function mouseUpHandler(
  event: React.MouseEvent,
  textAreaRef: React.RefObject<HTMLTextAreaElement | null> | undefined,
  setTextOverlayShouldBeVisible: (shouldRender: boolean) => void,
  setTextSelection: ({
    start,
    end,
    keyboard,
  }: {
    start: number
    end: number
    keyboard: boolean
    displayStart: boolean
  }) => void,
  cursorClickStartRef: React.RefObject<{startX: number; startY: number}> | undefined,
  shouldUseCursor: boolean,
  parentRef: React.RefObject<HTMLDivElement | null>,
  lineHeight: number,
) {
  if (!shouldUseCursor || event.defaultPrevented || !cursorClickStartRef) return

  if (event.button === 2) {
    event.preventDefault()
    event.stopPropagation()
    //it was a right click
  } else if (event.button === 0) {
    setTextOverlayShouldBeVisible(false)

    const isSmallScreen = window.matchMedia(`(max-width: ${ScreenSize.medium - 1}px)`).matches
    // Cache rect once to avoid layout thrashing - all reads happen synchronously
    const parentRect = parentRef.current?.getBoundingClientRect()

    const scrollTopOffset = parentRect?.top
      ? window.scrollY + parentRect.top
      : isSmallScreen
        ? approximateBlobYOffsetMobile
        : approximateBlobYOffsetDesktop
    // Check if it the click is in the lines excluding the scroll bar
    if (parentRef.current && event.pageY > scrollTopOffset + parentRef.current?.clientHeight) {
      cursorClickStartRef.current = {startX: -2, startY: -2}
      return
    }
    const lineNumberOfClick = calculateLineNumberFromOffset(event.pageY, scrollTopOffset, lineHeight)
    const leftClickOffset = parentRect?.left || 0
    const xOffset = event.clientX - leftClickOffset - minLeftOffsetBlob

    let displayAtStart = false
    if (
      lineNumberOfClick < cursorClickStartRef.current.startY ||
      (lineNumberOfClick === cursorClickStartRef.current.startY && xOffset < cursorClickStartRef.current.startX)
    ) {
      displayAtStart = true
    }
    requestAnimationFrame(() => {
      if (textAreaRef && textAreaRef.current) {
        setTextSelection({
          start: textAreaRef.current.selectionStart,
          end: textAreaRef.current.selectionEnd,
          keyboard: false,
          displayStart: displayAtStart,
        })
      }
    })
  }
}
function mouseDownHandler(
  event: React.MouseEvent,
  parentRef: React.RefObject<HTMLDivElement | null>,
  shouldUseCursor: boolean,
  cursorClickStartRef: React.RefObject<{startX: number; startY: number}> | undefined,
  lineHeight: number,
) {
  if (!shouldUseCursor || event.defaultPrevented || !cursorClickStartRef) return
  if (event.button === 2) {
    event.preventDefault()
    event.stopPropagation()
    return
    //it was a right click
  } else if (event.button === 0) {
    //it was a left click

    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (event.ctrlKey) {
      //it was a right click essentially
      event.preventDefault()
      event.stopPropagation()
      return
    }

    const isSmallScreen = window.matchMedia(`(max-width: ${ScreenSize.medium - 1}px)`).matches
    // Cache rect once to avoid layout thrashing - all reads happen synchronously
    const parentRect = parentRef.current?.getBoundingClientRect()

    const scrollTopOffset = parentRect?.top
      ? window.scrollY + parentRect.top
      : isSmallScreen
        ? approximateBlobYOffsetMobile
        : approximateBlobYOffsetDesktop
    // Check if it the click is in the lines excluding the scroll bar
    if (parentRef.current && event.pageY > scrollTopOffset + parentRef.current?.clientHeight) {
      cursorClickStartRef.current = {startX: -2, startY: -2}
      return
    }
    const lineNumberOfClick = calculateLineNumberFromOffset(event.pageY, scrollTopOffset, lineHeight)
    const leftClickOffset = parentRect?.left || 0
    const xOffset = event.clientX - leftClickOffset - minLeftOffsetBlob
    cursorClickStartRef.current = {startX: xOffset, startY: lineNumberOfClick}
  }
}
